// services/databaseService.ts
import { useSupabase } from '@/composables/use-supabase'
import type { BenefitSet, BenefitSetMember } from '@/types/benefitSets'
import type { Organization, Member } from '@/types/organization'

export type OrganizationProfilePatch = {
  logo_url?: string | null
  website?: string | null
  welcome_message?: string | null
  company_benefits?: string | null
}

export const useDatabaseService = () => {
  const { supabase } = useSupabase()

  // ========== Organization Operations ==========

  const updateOrganization = async (
    organizationId: string,
    patch: OrganizationProfilePatch,
  ): Promise<Organization> => {
    const { data, error } = await supabase
      .from('org__organizations')
      .update(patch)
      .eq('organization_id', organizationId)
      .select()
      .single<Organization>()

    if (error) throw error
    return data
  }

  // One object per org at a fixed, extensionless key so re-uploading any format
  // overwrites in place (upsert) — no orphaned objects from format changes. The
  // bucket serves the stored Content-Type header, so <img> renders without an
  // extension in the URL.
  const ORG_LOGO_PATH = (organizationId: string) => `${organizationId}/logo`

  const uploadOrgLogo = async (organizationId: string, file: File) => {
    const path = ORG_LOGO_PATH(organizationId)

    const { error: uploadError } = await supabase.storage
      .from('org-logos')
      .upload(path, file, { upsert: true, contentType: file.type })

    if (uploadError) throw uploadError

    const { data: urlData } = supabase.storage.from('org-logos').getPublicUrl(path)
    const logoUrl = `${urlData.publicUrl}?v=${Date.now()}`

    await updateOrganization(organizationId, { logo_url: logoUrl })
    return logoUrl
  }

  const deleteOrgLogo = async (organizationId: string, logoUrl: string | null) => {
    if (logoUrl) {
      // Best-effort storage removal: derive the object key from the public URL
      // written by uploadOrgLogo (strip the ?v= cache-buster). We intentionally
      // do not throw on a failed remove — clearing the stale logo_url below is
      // more important than a lingering object.
      const path = logoUrl.split('/org-logos/')[1]?.split('?')[0]
      if (path) {
        await supabase.storage.from('org-logos').remove([path])
      }
    }
    await updateOrganization(organizationId, { logo_url: null })
  }

  // ========== Member Operations ==========

  const getMembers = async (organizationId: string) => {
    // PostgREST caps every response at max_rows (1000) with no error — page
    // through so a large org's roster doesn't silently truncate.
    const PAGE = 1000
    const members: Member[] = []
    for (let offset = 0; ; offset += PAGE) {
      const { data, error } = await supabase
        .from('org__members_with_users')
        .select()
        .eq('organization_id', organizationId)
        .order('member_id')
        .range(offset, offset + PAGE - 1)
        .returns<Member[]>()

      if (error) throw error
      members.push(...(data || []))
      if (!data || data.length < PAGE) return members
    }
  }

  const updateMemberRole = async (memberId: number, newRole: 'member' | 'admin') => {
    const { data, error } = await supabase
      .from('org__members')
      .update({ role: newRole })
      .eq('member_id', memberId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // ========== SuperAdmin Operations ==========

  const getAllOrganizations = async (): Promise<Organization[]> => {
    const { data, error } = await supabase.rpc('get_all_organizations')
    if (error) throw error
    return (data as Organization[] | null) || []
  }

  const deleteOrganization = async (organizationId: string) => {
    const { error } = await supabase.rpc('delete_organization', {
      org_id: organizationId,
    })
    if (error) throw error
  }

  // ========== Benefit Sets ==========

  const BENEFIT_SET_COLUMNS =
    'benefit_set_id, organization_id, name, is_default, company_benefits'

  const listBenefitSets = async (organizationId: string): Promise<BenefitSet[]> => {
    const { data, error } = await supabase
      .from('org__benefit_sets')
      .select(BENEFIT_SET_COLUMNS)
      .eq('organization_id', organizationId)
      .order('is_default', { ascending: false })
      .order('name')
      .returns<BenefitSet[]>()
    if (error) throw error
    return data ?? []
  }

  const createBenefitSet = async (
    organizationId: string,
    name: string,
  ): Promise<BenefitSet> => {
    const { data, error } = await supabase
      .from('org__benefit_sets')
      .insert({ organization_id: organizationId, name, is_default: false })
      .select(BENEFIT_SET_COLUMNS)
      .single<BenefitSet>()
    if (error) throw error
    return data
  }

  const renameBenefitSet = async (benefitSetId: string, name: string): Promise<void> => {
    const { error } = await supabase
      .from('org__benefit_sets')
      .update({ name })
      .eq('benefit_set_id', benefitSetId)
    if (error) throw error
  }

  const setDefaultBenefitSet = async (benefitSetId: string): Promise<void> => {
    const { error } = await supabase.rpc('set_default_benefit_set', {
      p_benefit_set_id: benefitSetId,
    })
    if (error) throw error
  }

  const deleteBenefitSet = async (benefitSetId: string): Promise<void> => {
    const { error } = await supabase
      .from('org__benefit_sets')
      .delete()
      .eq('benefit_set_id', benefitSetId)
    if (error) throw error
  }

  const listBenefitSetMembers = async (
    organizationId: string,
  ): Promise<BenefitSetMember[]> => {
    const { data: sets, error: setsError } = await supabase
      .from('org__benefit_sets')
      .select('benefit_set_id')
      .eq('organization_id', organizationId)
      .returns<Array<{ benefit_set_id: string }>>()
    if (setsError) throw setsError
    const setIds = (sets ?? []).map((s) => s.benefit_set_id)
    if (!setIds.length) return []

    const { data, error } = await supabase
      .from('org__benefit_set_members')
      .select('user_id, benefit_set_id')
      .in('benefit_set_id', setIds)
      .returns<BenefitSetMember[]>()
    if (error) throw error
    return data ?? []
  }

  // ========== Benefits Operations ==========

  const listOrgBenefits = async (organizationId: string, benefitSetId: string) => {
    const { data, error } = await supabase
      .from('org__benefits')
      .select('benefit_id, category, label, short_description, long_description, source')
      .eq('organization_id', organizationId)
      .eq('benefit_set_id', benefitSetId)
      .eq('is_active', true)
      .order('category')
    if (error) throw error
    return data ?? []
  }

  const upsertOrgBenefit = async (
    organizationId: string,
    benefit: {
      benefit_id?: string
      benefit_set_id: string
      category: string
      label: string
      short_description: string | null
      long_description: string | null
    },
  ) => {
    const row = {
      ...benefit,
      organization_id: organizationId,
      source: benefit.benefit_id ? 'hr_edited' : 'manual',
    }
    const { data, error } = await supabase
      .from('org__benefits').upsert(row).select().single()
    if (error) throw error
    return data
  }

  const deleteOrgBenefit = async (benefitId: string) => {
    const { error } = await supabase.from('org__benefits').delete().eq('benefit_id', benefitId)
    if (error) throw error
  }

  return {
    // Organizations
    updateOrganization,
    uploadOrgLogo,
    deleteOrgLogo,

    // Members
    getMembers,
    updateMemberRole,

    // SuperAdmin
    getAllOrganizations,
    deleteOrganization,

    // Benefit sets
    listBenefitSets,
    createBenefitSet,
    renameBenefitSet,
    setDefaultBenefitSet,
    deleteBenefitSet,
    listBenefitSetMembers,

    // Benefits
    listOrgBenefits,
    upsertOrgBenefit,
    deleteOrgBenefit,
  }
}

